import { splitBlanks } from "./blanks";

// Reads the simple Markdown that document and spreadsheet makes are written in.
// Only what the model is asked to use is supported: headings, bold, italics,
// lists, tables and rules. Runs of underscores are left alone, they're blanks to fill in.

const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const BULLET = /^\s*[-*+•]\s+(.*)$/;
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/;
const RULE = /^\s*([-*])(\s*\1){2,}\s*$/;
const TABLE_ROW = /^\s*\|.*\|?\s*$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function tableCells(line) {
  let row = line.trim();
  if (row.startsWith("|")) row = row.slice(1);
  if (row.endsWith("|")) row = row.slice(0, -1);
  return row.split("|").map((cell) => cell.trim());
}

// Splits text into blocks:
// { type: "heading", level, text } | { type: "paragraph", text }
// { type: "list", ordered, start, items: [text] } | { type: "table", header, rows }
// { type: "rule" }
export function parseBlocks(text = "") {
  const blocks = [];
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  let paragraph = [];
  let list = null;
  let table = null;

  const flush = () => {
    if (paragraph.length) {
      blocks.push({ type: "paragraph", text: paragraph.join("\n") });
      paragraph = [];
    }
    if (list) {
      blocks.push(list);
      list = null;
    }
    if (table) {
      blocks.push(table);
      table = null;
    }
  };

  for (const line of lines) {
    if (!line.trim()) {
      flush();
      continue;
    }
    if (TABLE_ROW.test(line) && line.includes("|")) {
      if (!table) {
        flush();
        table = { type: "table", header: tableCells(line), rows: [] };
      } else if (!TABLE_DIVIDER.test(line)) {
        table.rows.push(tableCells(line));
      }
      continue;
    }
    if (table) flush();

    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({
        type: "heading",
        level: heading[1].length,
        text: heading[2],
      });
      continue;
    }
    if (RULE.test(line)) {
      flush();
      blocks.push({ type: "rule" });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) {
        flush();
        list = {
          type: "list",
          ordered,
          start: ordered ? Number(numbered[1]) : 1,
          items: [],
        };
      }
      list.items.push(ordered ? numbered[2] : bullet[1]);
      continue;
    }
    if (list) flush();
    paragraph.push(line);
  }
  flush();
  return blocks;
}

// Splits a line into runs of { text, bold, italic }, plus { blank } for each
// {{blank}} to fill in, whose text is the description in square brackets
export function parseInline(text = "") {
  return parseEmphasis(text).flatMap((run) =>
    splitBlanks(run.text).map((part) =>
      part.blank === undefined
        ? { ...run, text: part.text }
        : { ...run, text: `[${part.blank}]`, blank: part.blank }
    )
  );
}

function parseEmphasis(text = "") {
  const runs = [];
  // Only asterisks: underscores are too often blanks to fill in
  const pattern = /\*\*(.+?)\*\*|\*(?!\s)(.+?)\*/g;
  let last = 0;
  let match;
  while ((match = pattern.exec(text))) {
    if (match.index > last) {
      runs.push({ text: text.slice(last, match.index) });
    }
    if (match[2] !== undefined) {
      runs.push({ text: match[2], italic: true });
    } else {
      runs.push({ text: match[1], bold: true });
    }
    last = pattern.lastIndex;
  }
  if (last < text.length) {
    runs.push({ text: text.slice(last) });
  }
  return runs;
}

export function stripInline(text = "") {
  return parseInline(text)
    .map((run) => run.text)
    .join("");
}

// Plain text version for copying and sharing
export function toPlainText(text = "") {
  return parseBlocks(text)
    .map((block) => {
      switch (block.type) {
        case "heading":
        case "paragraph":
          return stripInline(block.text);
        case "list":
          return block.items
            .map(
              (item, index) =>
                `${
                  block.ordered ? `${block.start + index}.` : "•"
                } ${stripInline(item)}`
            )
            .join("\n");
        case "table":
          return [block.header, ...block.rows]
            .map((row) => row.map(stripInline).join("\t"))
            .join("\n");
        default:
          return "";
      }
    })
    .filter(Boolean)
    .join("\n\n");
}

// Best guess at a format when the model didn't say
export function guessOutput(text = "") {
  const blocks = parseBlocks(text);
  const tables = blocks.filter((block) => block.type === "table");
  const prose = blocks.filter((block) => block.type === "paragraph");
  if (tables.length && tables.length >= prose.length) return "xlsx";
  if (
    blocks.some(
      (block) =>
        block.type === "heading" ||
        block.type === "table" ||
        /\*\*/.test(block.text)
    )
  ) {
    return "docx";
  }
  return "text";
}
